import { fetchDeliveryWindowsByBuyerIdRequest, getDeliveryWindowsByBuyerId, getIsDeliveryWindowsLoading } from '@app/ducks';
import { getAccountNameWithId, getBuyerFullName } from '@app/logic/accounts';
import { getHumanTimePeriod } from '@app/logic/deliveryWindows';
import { getSortByTuple } from '@app/logic/documents';
import { useCollections } from '@app/services/hooks/collections/useCollections';
import { useLinesheetDownload } from '@app/services/hooks/linesheet/useLinesheetDownload';
import { useLinesheetTemplates } from '@app/services/hooks/linesheet/useLinesheetTemplates';
import { useSizeMappings } from '@app/services/hooks/units/useSizeMappings';
import { useAmplitude } from '@app/utils/hooks';
import { useBuyerSelect } from '@app/utils/hooks/selection/useBuyerSelect';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { getDeliveryWindowsSchema } from '@app/utils/schemas';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { Linesheet } from '../../../../typings';
import { Button } from '../various/Button';
import { Option } from '../various/Fields/Select';
import { AutocompleteField, Form, FormFieldset, FormGrid } from '../various/Form';
import { PageTitle } from '../various/PageTitle';

import { LinesheetGeneratorOptions, LinesheetGeneratorTemplates } from './components';
const DEFAULT_TEMPLATE_ID = '1';

export const Linesheets = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'buyers', 'linesheets', 'validation']);
  const { trackEvent } = useAmplitude();

  const { mutate, isPending } = useLinesheetDownload();
  const { data: linesheets, isLoading: isLoadingTemplates } = useLinesheetTemplates();
  const { data: sizeMappingsData, isLoading: isLoadingSizeMappings } = useSizeMappings();
  const { options, isLoading: isLoadingBuyers, handleLoadMore, handleSearchChange } = useBuyerSelect();

  const sizeMappings = React.useMemo(() => sizeMappingsData?.data ?? [], [sizeMappingsData]);
  const templates = linesheets?.templates ?? {};
  const defaultLinesheetTemplate = templates[DEFAULT_TEMPLATE_ID] as Maybe<Linesheet.TemplateData>;

  const defaultValues: Linesheet.FormData = {
    attributes: defaultLinesheetTemplate?.attributes ?? [],
    buyer: null,
    collections: [],
    deliveryWindows: [],
    groupBy: defaultLinesheetTemplate?.groupBy ?? null,
    linesheetTemplate: DEFAULT_TEMPLATE_ID,
    showCustomerPrice: true,
    showStockValues: true,
    skipOutOfStock: false,
    sortBy: defaultLinesheetTemplate?.sortBy ?? null,
    thenSortBy: defaultLinesheetTemplate?.thenSortBy ?? 'none',
    units: [],
  };

  const validationSchema: yup.ObjectSchema<Pick<Linesheet.FormData, 'buyer' | 'deliveryWindows'>> = yup.object({
    buyer: yup.string().required(t('validation:select_buyer_hint')),
    deliveryWindows: getDeliveryWindowsSchema(),
  });

  const formMethods = useValidatedForm({ defaultValues, validationSchema });
  const { setValue, watch } = formMethods;

  const [buyerId, deliveryWindowIds, selectedTemplateId] = watch(['buyer', 'deliveryWindows', 'linesheetTemplate']);

  const deliveryWindowsSelector = React.useMemo(() => getDeliveryWindowsByBuyerId(buyerId), [buyerId]);
  const deliveryWindowsForBuyer = useSelector(deliveryWindowsSelector);
  const isLoadingDeliveryWindows = useSelector(getIsDeliveryWindowsLoading);

  const { data: collectionsData, isLoading: isLoadingCollections } = useCollections({ buyerId, deliveryWindowIds });

  const handleBuyerChange = (buyer: string) => {
    dispatch(fetchDeliveryWindowsByBuyerIdRequest({ buyerId: buyer }));
    setValue('deliveryWindows', []);
    setValue('collections', []);
  };

  const onDeliveryWindowsChange = () => {
    setValue('collections', []);
  };

  const handleSubmit = React.useCallback(
    (values: Linesheet.FormData) => {
      const { showCustomerPrice, showStockValues, linesheetTemplate, groupBy, sortBy, thenSortBy, attributes, ...rest } = values;

      mutate(
        {
          showCustomerPriceFlag: showCustomerPrice,
          showStock: showStockValues,
          template: linesheetTemplate,
          templateDO: {
            groupBy,
            productAttributes: attributes,
            sortBy: getSortByTuple(sortBy, thenSortBy),
          },
          ...rest,
        },
        {
          onSuccess: () => {
            trackEvent({ name: 'downloadAction.button.linesheet.submit', properties: { path: 'linesheetGenerator' } });
          },
        },
      );
    },
    [mutate, trackEvent],
  );

  const showNoTemplateMessage = isEmpty(templates) && !isLoadingTemplates;

  return (
    <PageTitle title={t('common:linesheet_other')}>
      <Form formMethods={formMethods} onSubmit={handleSubmit}>
        <div className="mb-16 w-full px-4 lg:px-8">
          <div className="mx-auto flex max-w-screen-lg flex-1 flex-col">
            <div className="sticky top-[--headerHeight] z-topBar flex items-center justify-between gap-2 bg-neutral-96 py-2 transition-all group-[.scrollingDown]/body:top-0 lg:mb-0">
              <h2 className="flex-1 heading-2 sm:flex-initial md:heading-1">{t('linesheets:linesheet_generator')}</h2>
              <Button type="submit" isLoading={isPending} disabled={showNoTemplateMessage || isLoadingTemplates}>
                {t('common:generate')}
              </Button>
            </div>
            {showNoTemplateMessage ?
              <span className="flex h-full items-center justify-center pt-3">{t('linesheets:no_linesheet_templates_seller')}</span>
            : <section>
                <FormFieldset isDisabled={isPending}>
                  <FormGrid rowGap="large">
                    <div className="mt-2 flex flex-col gap-5 rounded-regular bg-white p-4 lg:px-5 lg:py-5">
                      <h3 className="heading-3">{t('common:settings')}</h3>
                      <p className="text-neutral-40 body-2">{t('linesheets:linesheets_settings_info')}</p>
                      <LinesheetGeneratorTemplates templates={templates} isLoading={isLoadingTemplates} />
                      <AutocompleteField
                        isRequired
                        label={t('buyers:buyer_one')}
                        isLoadingOptions={isLoadingBuyers}
                        name="buyer"
                        placeholder={t('buyers:select_buyer')}
                        helpMessage={t('linesheets:choose_buyer.hint')}
                        onChange={handleBuyerChange}
                        onSearchChange={handleSearchChange}
                        onLoadMore={handleLoadMore}
                      >
                        {options.map(buyer => (
                          <Option maskData key={buyer.id} value={buyer.id} description={getAccountNameWithId(buyer)}>
                            {getBuyerFullName(buyer)}
                          </Option>
                        ))}
                      </AutocompleteField>
                      <AutocompleteField
                        isRequired
                        isDisabled={!isDefined(buyerId)}
                        isLoadingOptions={isLoadingDeliveryWindows}
                        name="deliveryWindows"
                        helpMessage={t('linesheets:choose_collection.hint')}
                        label={t('linesheets:choose_delwin.label')}
                        placeholder={t('linesheets:choose_delwin.placeholder')}
                        onChange={onDeliveryWindowsChange}
                      >
                        {deliveryWindowsForBuyer.map(delwin => (
                          <Option
                            key={delwin.deliveryWindow}
                            value={delwin.deliveryWindow}
                            description={getHumanTimePeriod(delwin).deliveryPeriod ?? undefined}
                          >
                            {delwin.name}
                          </Option>
                        ))}
                      </AutocompleteField>
                      <AutocompleteField
                        isLoadingOptions={isLoadingCollections}
                        label={t('linesheets:choose_collection.label')}
                        name="collections"
                        helpMessage={t('linesheets:choose_collection.hint')}
                        placeholder={t('linesheets:choose_collection.placeholder')}
                        isDisabled={isEmpty(deliveryWindowIds)}
                      >
                        {collectionsData?.collections.map(({ name, collection }) => (
                          <Option key={collection} value={collection}>
                            {name}
                          </Option>
                        ))}
                      </AutocompleteField>
                      <AutocompleteField
                        isLoadingOptions={isLoadingSizeMappings}
                        label={t('linesheets:choose_units.label')}
                        name="units"
                        helpMessage={t('linesheets:choose_units.hint')}
                        placeholder={t('linesheets:choose_units.placeholder')}
                      >
                        {sizeMappings?.map(unit => (
                          <Option key={unit.id} value={unit.name}>
                            {unit.displayName}
                          </Option>
                        ))}
                      </AutocompleteField>
                    </div>
                    <div className="mt-2 flex flex-col gap-5 rounded-regular bg-white p-4 lg:px-5 lg:py-5">
                      <h3 className="heading-3">{t('linesheets:additional_options')}</h3>
                      <p className="text-neutral-40 body-2">{t('linesheets:linesheets_settings_additional_info')}</p>
                      <LinesheetGeneratorOptions selectedTemplate={templates[selectedTemplateId]} />
                    </div>
                  </FormGrid>
                </FormFieldset>
              </section>
            }
          </div>
        </div>
      </Form>
    </PageTitle>
  );
};
